/**
 * UnifiedDocumentUpload.jsx — Единна точка за качване (Option B)
 *
 * Един модал за КАКЪВТО И ДА Е здравен документ (лабораторно PDF/снимка,
 * DUTCH/ДНК, епикриза). Изпраща към /api/documents/upload — backend AI
 * класификаторът решава типа и го записва в правилната колекция. UI-ът
 * показва различен резултат според `classified_as`.
 */
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  Upload, X, Loader2, CheckCircle2, AlertCircle, Dna, FlaskConical,
  FileText, Stethoscope, Sparkles, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { emitHealthEvent } from "@/utils/healthEvents";
import { toast } from "sonner";
import LabImpactPanel from "@/components/breast_intel/LabImpactPanel";

const TX = {
  bg: {
    title: "Качи документ",
    subtitle:
      "Качете какъвто и да е документ — лабораторно изследване, DUTCH/ДНК тест или епикриза. Ще разпознаем автоматично типа и ще го подредим на правилното място.",
    drag: "Пуснете файл тук или кликнете да изберете",
    formats: "PDF, JPG, PNG, WEBP",
    typeHint: "Тип (по избор):",
    auto: "🪄 Автоматично",
    lab: "Лаборатория",
    dutch: "DUTCH",
    genomic: "ДНК",
    epicrisis: "Епикриза",
    uploading: "Разпознаваме документа…",
    analysingLab: "Анализираме лабораторното изследване… (може да отнеме до 60 сек)",
    error: "Грешка при обработката",
    retry: "Опитайте отново с друг файл",
    confidence: "Сигурност",
    flagsFound: "намерени маркери",
    another: "Качи още един",
    close: "Затвори",
    privacy:
      "Файловете се обработват чрез сигурен AI. Пазят се само Вашите структурирани резултати и можете да ги изтриете по всяко време.",
    classified: {
      dutch: "Разпознат: DUTCH хормонален тест",
      genomic: "Разпознат: ДНК / генетичен панел",
      standard_lab: "Разпознато: лабораторно изследване",
      epicrisis: "Разпозната: епикриза / лекарски документ",
    },
    epi: {
      doctor: "Лекар",
      date: "Дата",
      diagnoses: "Документирани диагнози",
      meds: "Медикаменти",
      recs: "Препоръки",
      followup: "Проследяване",
      saved: "Епикризата е запазена в профила Ви",
      suggestedTitle: "🧬 AI-предложени диагнози → KEGG пътища",
      suggestedIntro: "Ще ги свържа с метаболитно/възпалителните сигнални пътища. Приемете, за да участват в BHI.",
      acceptAll: "Приеми всички",
      accept: "Приеми",
      accepted: "Приета",
      accepting: "Записваме…",
      acceptSuccess: "Диагнозите са добавени в профила Ви",
      acceptError: "Грешка при приемане",
    },
    labResult: {
      done: "Анализът е готов",
      findings: "маркери извлечени",
      view: "Резултатите са запазени в „Лаборатории“",
    },
  },
};

const TYPE_META = {
  dutch: { Icon: FlaskConical, color: "text-purple-500" },
  genomic: { Icon: Dna, color: "text-indigo-500" },
  standard_lab: { Icon: FileText, color: "text-blue-500" },
  epicrisis: { Icon: Stethoscope, color: "text-amber-500" },
};

export default function UnifiedDocumentUpload({ open, onClose, lang = "bg", initialHint = "", onDone }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const t = TX[lang] || TX.bg;
  const [hint, setHint] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | uploading | analysing | done | error
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [drag, setDrag] = useState(false);
  const [acceptedIds, setAcceptedIds] = useState([]);
  const [accepting, setAccepting] = useState(false);
  const [elapsed, setElapsed] = useState(0);   // iter932 — seconds since analysing started
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);

  useEffect(() => {
    if (open) {
      setHint(initialHint || ""); setPhase("idle"); setResult(null); setErr(null);
      setAcceptedIds([]); setAccepting(false); setElapsed(0);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (elapsedRef.current) clearInterval(elapsedRef.current);
    };
  }, [open, initialHint]);

  // iter932 — start elapsed-timer whenever analysing kicks in
  useEffect(() => {
    if (phase === "analysing" || phase === "uploading") {
      if (!elapsedRef.current) {
        elapsedRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
      }
    } else {
      if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
      if (phase === "idle" || phase === "done") setElapsed(0);
    }
  }, [phase]);

  const pollLabTask = (taskId) => {
    setPhase("analysing");
    let tries = 0;
    let lastStatus = "";  // iter932 — track backend phase for richer UI feedback
    pollRef.current = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/analyze/lab-report/${taskId}`);
        // iter932 — surface backend phase (e.g. "ocr", "extracting", "diagnosing")
        if (data.status && data.status !== lastStatus) {
          lastStatus = data.status;
          setPhase(
            data.status === "ready" ? "done"
            : data.status === "failed" ? "error"
            : "analysing"
          );
        }
        if (data.status === "ready") {
          clearInterval(pollRef.current);
          const payload = {
            classified_as: "standard_lab",
            analysis: data.analysis,
            lab_analysis_id: data.lab_analysis_id,
            auto_diagnosis_snapshot_id: data.auto_diagnosis_snapshot_id,
            auto_diagnosis_count: data.auto_diagnosis_count,
          };
          setResult(payload);
          setPhase("done");
          onDone?.(payload);
          emitHealthEvent("auto-diagnosis-snapshot-ready", {
            snapshot_id: data.auto_diagnosis_snapshot_id,
            count: data.auto_diagnosis_count || 0,
            task_id: taskId,
          });
        } else if (data.status === "failed") {
          clearInterval(pollRef.current);
          setErr(data.error || "Анализът неуспешен");
          setPhase("error");
        }
      } catch (e) {
        // keep polling a few times before giving up (network hiccup)
      }
      // iter932 — extended from 40 → 120 (was 2 min, now 6 min). Real
      // multi-page PDFs with LLM extraction + snapshot generation can
      // legitimately take 3-5 min. User complained they waited "several
      // minutes" before seeing "Времето изтече".
      if (tries > 120) {
        clearInterval(pollRef.current);
        setErr("Анализът отнема необичайно дълго. Опреснете страницата — резултатът вероятно вече е готов и запазен.");
        setPhase("error");
      }
    }, 3000);
  };

  const upload = async (file) => {
    if (!file) return;
    setPhase("uploading"); setErr(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (hint) fd.append("hint", hint);
      const { data } = await axios.post(`${API}/documents/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 130000,
      });
      if (data.classified_as === "standard_lab" && data.task_id) {
        pollLabTask(data.task_id);
      } else {
        setResult(data);
        setPhase("done");
        onDone?.(data);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
      setPhase("error");
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  if (!open) return null;

  const cls = result?.classified_as;
  const meta = TYPE_META[cls] || {};

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="unified-doc-modal"
    >
      <div
        className={`max-w-2xl w-full max-h-[88vh] overflow-y-auto rounded-2xl border p-6 shadow-2xl ${
          isDark ? "bg-[hsl(222,47%,11%)] border-white/10 text-white" : "bg-white border-gray-200"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-extrabold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-pink-500" /> {t.title}
          </h3>
          <button onClick={onClose} className="opacity-60 hover:opacity-100" data-testid="unified-doc-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {phase === "idle" && (
          <>
            <p className={`text-xs mb-4 ${isDark ? "text-gray-300" : "text-gray-600"}`}>{t.subtitle}</p>
            <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
              <span className="opacity-70">{t.typeHint}</span>
              {[["", t.auto], ["standard_lab", t.lab], ["dutch", t.dutch], ["genomic", t.genomic], ["epicrisis", t.epicrisis]].map(([v, l]) => (
                <button
                  key={v}
                  onClick={() => setHint(v)}
                  className={`px-2.5 py-1 rounded-full font-semibold transition-colors ${
                    hint === v
                      ? "bg-pink-500 text-white"
                      : (isDark ? "bg-white/5 hover:bg-white/10" : "bg-gray-100 hover:bg-gray-200")
                  }`}
                  data-testid={`unified-hint-${v || "auto"}`}
                >{l}</button>
              ))}
            </div>

            <label
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={onDrop}
              className={`block cursor-pointer p-10 rounded-xl border-2 border-dashed text-center transition-colors ${
                drag
                  ? "border-pink-500 bg-pink-500/10"
                  : (isDark ? "border-white/20 hover:border-white/40" : "border-gray-300 hover:border-gray-500")
              }`}
              data-testid="unified-drop"
            >
              <Upload className="w-10 h-10 mx-auto opacity-50 mb-2" />
              <p className="text-sm font-semibold">{t.drag}</p>
              <p className="text-[11px] opacity-50 mt-1">{t.formats}</p>
              <input
                type="file" accept="application/pdf,image/*" className="hidden"
                onChange={(e) => upload(e.target.files?.[0])}
                data-testid="unified-file-input"
              />
            </label>
            <p className="text-[10px] opacity-60 mt-2 italic">{t.privacy}</p>
          </>
        )}

        {(phase === "uploading" || phase === "analysing") && (
          <div className="text-center py-10" data-testid="unified-loading">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-pink-500" />
            {/* iter932 — Progressive phase text based on elapsed time. Real
                multi-page PDFs need 60-180s (OCR → LLM extract → snapshot). */}
            <p className="mt-3 text-sm">
              {phase === "uploading"
                ? t.uploading
                : elapsed < 20  ? "🔍 Разчитаме текста от документа…"
                : elapsed < 45  ? "🧬 Извличаме лабораторните маркери с AI…"
                : elapsed < 90  ? "🤖 Специалист-ботовете анализират модели…"
                : elapsed < 150 ? "🎯 Създаваме Вашия Digital Health Twin…"
                : "⏳ Обработка на голям документ — още малко търпение…"}
            </p>
            <p className="mt-1 text-[11px] opacity-70 tabular-nums">
              {elapsed}с изминали · обикновено 45-120с
            </p>
            {elapsed >= 120 && (
              <p className="mt-2 text-[10px] italic opacity-60 max-w-sm mx-auto">
                Многостранични PDF файлове с OCR отнемат повече. Можете спокойно да минимизирате прозореца — резултатът ще Ви чака.
              </p>
            )}
          </div>
        )}

        {phase === "error" && (
          <div className="p-4 rounded-xl bg-red-500/10 text-red-600 dark:text-red-400 flex items-start gap-2" data-testid="unified-error">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="text-sm space-y-2">
              <div className="font-semibold">{t.error}</div>
              <div className="text-xs leading-relaxed opacity-90">{err}</div>
              <button
                onClick={() => { setErr(null); setResult(null); setPhase("idle"); }}
                className="mt-2 px-3 py-1.5 rounded-lg text-xs font-bold bg-red-500/20 hover:bg-red-500/30"
                data-testid="unified-retry-btn"
              >{t.retry}</button>
            </div>
          </div>
        )}

        {phase === "done" && result && (
          <div className="space-y-3" data-testid="unified-result">
            <div className="p-4 rounded-xl bg-emerald-500/10 flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold flex items-center gap-1.5">
                  {meta.Icon && <meta.Icon className={`w-4 h-4 ${meta.color}`} />}
                  {t.classified[cls] || "Обработено"}
                </div>
                {(cls === "dutch" || cls === "genomic") && result.extraction_confidence && (
                  <div className="text-xs opacity-80 mt-0.5">{t.confidence}: {result.extraction_confidence}</div>
                )}
              </div>
            </div>

            {/* DUTCH / genomic → markers extracted + qualitative flags */}
            {(cls === "dutch" || cls === "genomic") && (
              Object.keys(result.flags || {}).length > 0 || (result.markers_extracted || 0) > 0
            ) && (
              <div className="p-3 rounded-xl border border-amber-500/30 bg-amber-500/5" data-testid="unified-dutch-flags">
                <div className="text-xs font-bold opacity-80 mb-2">
                  {(result.markers_extracted || 0) > 0
                    ? `${result.markers_extracted} ${t.flagsFound}`
                    : `${Object.keys(result.flags || {}).filter((k) => !k.endsWith("recommendation")).length} ${t.flagsFound}`}
                </div>
                <ul className="space-y-1.5 text-xs">
                  {Object.entries(result.flags || {}).filter(([k]) => k.endsWith("recommendation")).map(([k, v]) => (
                    <li key={k} className="flex items-start gap-1.5">
                      <span className="text-amber-500 shrink-0">▸</span><span>{v}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Epicrisis → summary */}
            {cls === "epicrisis" && (
              <div className="p-3 rounded-xl border border-amber-500/30 bg-amber-500/5 text-xs space-y-1.5" data-testid="unified-epicrisis-summary">
                {result.summary?.doctor && <div><b>{t.epi.doctor}:</b> {result.summary.doctor}{result.summary.specialty ? ` · ${result.summary.specialty}` : ""}</div>}
                {result.summary?.date && <div><b>{t.epi.date}:</b> {result.summary.date}</div>}
                {result.summary?.summary && <p className="leading-relaxed">{result.summary.summary}</p>}
                {Array.isArray(result.summary?.documented_diagnoses) && result.summary.documented_diagnoses.length > 0 && (
                  <div><b>{t.epi.diagnoses}:</b> {result.summary.documented_diagnoses.join(", ")}</div>
                )}
                {Array.isArray(result.summary?.medications) && result.summary.medications.length > 0 && (
                  <div><b>{t.epi.meds}:</b> {result.summary.medications.join(", ")}</div>
                )}
                {Array.isArray(result.summary?.recommendations) && result.summary.recommendations.length > 0 && (
                  <div><b>{t.epi.recs}:</b> {result.summary.recommendations.join("; ")}</div>
                )}
                {result.summary?.follow_up && <div><b>{t.epi.followup}:</b> {result.summary.follow_up}</div>}
                <div className="opacity-60 italic pt-1">{t.epi.saved}</div>
              </div>
            )}

            {/* Epicrisis → suggested conditions → KEGG pathways */}
            {cls === "epicrisis" && Array.isArray(result.suggested_conditions) && result.suggested_conditions.length > 0 && (
              <div className="p-3 rounded-xl border border-pink-500/30 bg-pink-500/5" data-testid="unified-suggested-conditions">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-pink-500" />
                    <span className="text-xs font-bold">{t.epi.suggestedTitle}</span>
                  </div>
                  {result.suggested_conditions.filter((s) => !acceptedIds.includes(s.condition_id)).length > 1 && (
                    <button
                      onClick={async () => {
                        setAccepting(true);
                        try {
                          const { data } = await axios.post(
                            `${API}/documents/epicrises/${result.epicrisis_id}/accept-conditions`,
                            {},
                          );
                          setAcceptedIds(data.accepted_condition_ids || []);
                          toast.success(`${t.epi.acceptSuccess} (${data.count})`);
                          emitHealthEvent("conditions-changed", { source: "epicrisis-modal-accept-all" });
                        } catch (e) {
                          toast.error(t.epi.acceptError);
                        } finally {
                          setAccepting(false);
                        }
                      }}
                      disabled={accepting}
                      className="px-2.5 py-1 rounded-md text-[10px] font-bold text-white bg-gradient-to-r from-pink-500 to-purple-500 hover:opacity-90 disabled:opacity-60 flex items-center gap-1"
                      data-testid="epi-accept-all"
                    >
                      {accepting ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                      {t.epi.acceptAll}
                    </button>
                  )}
                </div>
                <p className="text-[10px] opacity-70 leading-relaxed mb-2">{t.epi.suggestedIntro}</p>
                <ul className="space-y-2">
                  {result.suggested_conditions.map((s) => {
                    const isAccepted = acceptedIds.includes(s.condition_id);
                    return (
                      <li
                        key={s.condition_id}
                        className={`p-2 rounded-lg border text-[11px] ${
                          isAccepted
                            ? "border-emerald-500/40 bg-emerald-500/10"
                            : (isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50")
                        }`}
                        data-testid={`epi-suggested-${s.condition_id}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="font-bold truncate">{s.name_bg}</span>
                            {isAccepted && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-600 dark:text-emerald-300 font-bold shrink-0">
                                <CheckCircle2 className="w-3 h-3" /> {t.epi.accepted}
                              </span>
                            )}
                          </div>
                          {!isAccepted && (
                            <button
                              onClick={async () => {
                                setAccepting(true);
                                try {
                                  const { data } = await axios.post(
                                    `${API}/documents/epicrises/${result.epicrisis_id}/accept-conditions`,
                                    { condition_ids: [s.condition_id] },
                                  );
                                  setAcceptedIds(data.accepted_condition_ids || []);
                                  toast.success(t.epi.acceptSuccess);
                                  emitHealthEvent("conditions-changed", { source: "epicrisis-modal-accept-one" });
                                } catch (e) {
                                  toast.error(t.epi.acceptError);
                                } finally {
                                  setAccepting(false);
                                }
                              }}
                              disabled={accepting}
                              className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-pink-500/20 hover:bg-pink-500/30 text-pink-600 dark:text-pink-300 disabled:opacity-60 flex items-center gap-1 shrink-0"
                              data-testid={`epi-accept-${s.condition_id}`}
                            >
                              {accepting ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                              {t.epi.accept}
                            </button>
                          )}
                        </div>
                        {Array.isArray(s.matching_pathways) && s.matching_pathways.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {s.matching_pathways.map((p) => (
                              <a
                                key={p.id}
                                href={p.kegg ? `https://www.kegg.jp/pathway/${p.kegg}` : undefined}
                                target={p.kegg ? "_blank" : undefined}
                                rel="noopener noreferrer"
                                className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                                  isDark ? "bg-white/10 hover:bg-white/15 text-blue-200" : "bg-blue-50 hover:bg-blue-100 text-blue-700"
                                }`}
                                title={p.name_bg}
                              >
                                {p.name_bg}
                                {p.kegg && <ExternalLink className="w-2.5 h-2.5 opacity-60" />}
                              </a>
                            ))}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Standard lab → summary */}
            {cls === "standard_lab" && result.analysis && (
              <div className="p-3 rounded-xl border border-blue-500/30 bg-blue-500/5 text-xs space-y-1.5" data-testid="unified-lab-summary">
                <div className="font-bold">{t.labResult.done}</div>
                {result.analysis.summary && <p className="leading-relaxed opacity-90">{result.analysis.summary}</p>}
                {Array.isArray(result.analysis.findings) && (
                  <div className="opacity-70">{result.analysis.findings.length} {t.labResult.findings}</div>
                )}
                <div className="opacity-60 italic pt-1">{t.labResult.view}</div>
              </div>
            )}

            {/* iter883 — Standard lab: impact panel with suggested conditions → KEGG + tissue-zone deltas */}
            {cls === "standard_lab" && result.lab_analysis_id && (
              <LabImpactPanel labId={result.lab_analysis_id} lang={lang} />
            )}

            <div className="flex gap-2">
              <button
                onClick={() => { setResult(null); setErr(null); setPhase("idle"); }}
                className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-500/20 hover:opacity-90"
                data-testid="unified-another"
              >{t.another}</button>
              <button
                onClick={onClose}
                className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:opacity-90"
                data-testid="unified-done"
              >{t.close}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
